import { lazy, Suspense, Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ForbiddenErrorPopup from './components/ForbiddenErrorPopup';
import { useForbiddenError } from './hooks/useForbiddenError';

// Lazy load all route components to reduce initial bundle size
const Inbox = lazy(() => import('./pages/dashboard/inbox/inbox'));
const Connect = lazy(() => import('./pages/dashboard/connect/connect'));
const Contact = lazy(() => import('./pages/dashboard/contact/contact'));
const Settings = lazy(() => import('./pages/dashboard/settings/settings'));
const Dashboard = lazy(() => import('./pages/dashboard/dashboard'));
const Login = lazy(() => import('./pages/auth/login/login'));
const OTPVerification = lazy(() => import('./pages/auth/otp/otp'));
const ResetPassword = lazy(
  () => import('./pages/auth/reset_password/reset_password')
);
const VerifyEmail = lazy(
  () => import('./pages/auth/verify_email/verify_email')
);
const PersonalData = lazy(
  () => import('./pages/auth/personal_data/personal_data')
);
const GoogleSuccess = lazy(
  () => import('./pages/auth/google_success/google_success')
);
const GoogleFailed = lazy(
  () => import('./pages/auth/google_failed/google_failed')
);
const SelectNumber = lazy(
  () => import('./pages/auth/select_number/select_number')
);
const SelectSubscription = lazy(
  () => import('./pages/auth/select_subscription/select_subscription')
);
const PurchaseSuccess = lazy(
  () => import('./pages/auth/purchase_status/purchase_success')
);
const PurchaseFail = lazy(
  () => import('./pages/auth/purchase_status/purchase_fail')
);
const Myprofile = lazy(
  () => import('./pages/dashboard/settings/myprofile/myprofile')
);
const Myteam = lazy(() => import('./pages/dashboard/settings/myteam/myteam'));
const MyBusiness = lazy(
  () => import('./pages/dashboard/settings/mybusiness/mybusiness')
);
const BusinessWithContext = lazy(
  () => import('./pages/dashboard/settings/business/index')
);
const ReportsAndBilling = lazy(
  () => import('./pages/dashboard/settings/reportsandbilling/reportsandbilling')
);
const Reserved = lazy(() =>
  import('./pages/auth/reserved/reserved').then((module) => ({
    default: module.Reserved,
  }))
);
const PostLogin = lazy(() => import('./pages/auth/post_login/PostLogin'));

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
  </div>
);

class LazyLoadErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean; error: Error | null }
> {
  state: { hasError: boolean; error: Error | null } = {
    hasError: false,
    error: null,
  };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Lazy route failed to load', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message?.toLowerCase() || '';
      const isChunkError =
        message.includes('dynamically imported module') ||
        message.includes('loading chunk') ||
        message.includes('chunkloaderror');

      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gray-50 px-6 text-center">
          <h1 className="text-2xl font-semibold text-gray-900">
            {isChunkError
              ? "We can't load this screen right now"
              : 'Something went wrong'}
          </h1>
          <p className="max-w-lg text-sm text-gray-600">
            {isChunkError
              ? 'A supporting file failed to download, often because a server request returned an error. Please try again once the network issue clears.'
              : 'An unexpected error occurred while loading this part of the application.'}
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={this.handleRetry}
              className="rounded-md bg-[#78C7D1] px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-[#63b2bd]"
            >
              Try again
            </button>
            <button
              onClick={() => window.location.reload()}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-white"
            >
              Reload page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Wrapper component for lazy loading with Suspense
const withSuspense = (
  Component: React.LazyExoticComponent<
    React.ComponentType<Record<string, never>>
  >
) => (
  <LazyLoadErrorBoundary>
    <Suspense fallback={<LoadingSpinner />}>
      <Component />
    </Suspense>
  </LazyLoadErrorBoundary>
);

const router = createBrowserRouter([
  {
    path: '/login',
    element: withSuspense(Login),
  },
  {
    path: '/post-login',
    element: withSuspense(PostLogin),
  },
  {
    path: '/otp',
    element: withSuspense(OTPVerification),
  },
  {
    path: '/reset_password/:token',
    element: withSuspense(ResetPassword),
  },
  {
    path: '/verify_email/:token',
    element: withSuspense(VerifyEmail),
  },
  {
    path: '/select_number',
    element: withSuspense(SelectNumber),
  },
  {
    path: '/select_subscription',
    element: withSuspense(SelectSubscription),
  },
  {
    path: '/personal_data',
    element: withSuspense(PersonalData),
  },
  {
    path: '/google_success/:token',
    element: withSuspense(GoogleSuccess),
  },
  {
    path: '/google_failed',
    element: withSuspense(GoogleFailed),
  },
  {
    path: '/purchase_success',
    element: withSuspense(PurchaseSuccess),
  },
  {
    path: '/purchase_fail',
    element: withSuspense(PurchaseFail),
  },
  {
    path: '/reserved',
    element: withSuspense(Reserved),
  },
  {
    path: '/p/update-payment',
    element: <Navigate to="/settings/reports?tab=Plan" replace />,
  },
  {
    path: '/',
    element: withSuspense(Dashboard),
    children: [
      {
        path: 'inbox',
        element: withSuspense(Inbox),
      },
      {
        path: 'connect',
        element: withSuspense(Connect),
      },
      {
        path: 'contact',
        element: withSuspense(Contact),
      },
      {
        path: 'settings',
        element: withSuspense(Settings),
        children: [
          {
            index: true,
            element: <Navigate to="myprofile" />,
          },
          {
            path: 'myprofile',
            element: withSuspense(Myprofile),
          },
          {
            path: 'mybusiness',
            element: withSuspense(MyBusiness),
          },
          {
            path: 'myteam',
            element: withSuspense(Myteam),
          },
          {
            path: 'business',
            element: withSuspense(BusinessWithContext),
          },
          {
            path: 'reports',
            element: withSuspense(ReportsAndBilling),
          },
        ],
      },
    ],
  },
]);

function App() {
  const {
    error: forbiddenError,
    isOpen: isForbiddenOpen,
    closeError: closeForbiddenError,
  } = useForbiddenError();

  return (
    <>
      <RouterProvider router={router} />
      <Toaster position="top-right" />
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Global 403 Error Popup */}
      <ForbiddenErrorPopup
        isOpen={isForbiddenOpen}
        onClose={closeForbiddenError}
        message={
          forbiddenError?.message ||
          'Access forbidden. You do not have permission to perform this action.'
        }
      />
    </>
  );
}

export default App;
